import { FuelButton, FuelCallout, FuelTypography } from '@frete.com/fuel-react';
import { FuelDialog, FuelSpacer } from '@frete.com/fuel-station-react';
import { useAmplitude } from 'hooks/useAmplitude';
import { useAppContext } from 'hooks/useAppContext';
import { useGlobals } from 'hooks/useGlobals';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { postShareFreightWithTruckers } from 'services/freight';
import * as Styled from './styled';
import { TModalConfirmShareFreightsProps } from './types';

const ModalConfirmShareFreights: FC<TModalConfirmShareFreightsProps> = ({
  isOpen,
  handleOnClose,
  retryShareFreights,
}) => {
  const {
    state: {
      shareFreights: { truckers },
      suggestedTruckers: { dataSuggested },
      searchTruckers: { dataSearch },
    },
    dispatch,
  } = useAppContext();

  const globals = useGlobals();
  const companyId = globals?.user?.data?.company?.id;

  const driverTruckerAlreadyShared = truckers.filter((trucker) => trucker.isShared).length;

  const { sendAmplitude } = useAmplitude();
  const { freightId } = useParams();
  const toastSuccessRef = useRef(null);
  const toastErrorsRef = useRef(null);
  const [requestLoadingShare, setRequestLoadingShare] = useState(false);

  const handleOpenToastSuccess = () => {
    sendAmplitude('trucker_listing_share_freight_clicked', {
      source: 'botao-compartilhar-modal-compartilhamento',
      companyId,
    });

    setRequestLoadingShare(true);
    const truckersIds = truckers.filter((trucker) => !trucker.isShared).map((trucker) => trucker.uuid);
    postShareFreightWithTruckers({ encodedFreightId: freightId, truckersId: truckersIds })
      .then(() => {
        const newDataWithSharedIcon = dataSuggested.map((trucker) => ({
          ...trucker,
          person: {
            ...trucker.person,
            alreadyNotified: trucker.person.alreadyNotified || truckersIds.includes(trucker.person.uuid),
          },
        }));

        if (!dataSearch) {
          dispatch({ type: 'suggested_truckers_set_shared', payload: newDataWithSharedIcon });
        }

        handleOnClose();
        toastSuccessRef.current.open();
        dispatch({ type: 'remove_all_selected_truckers' });
      })
      .catch((err) => {
        handleOnClose();
        toastErrorsRef.current.open();

        return err;
      })
      .finally(() => {
        setRequestLoadingShare(false);
      });
  };

  const handleErrorRequestToast = () => {
    retryShareFreights();

    toastErrorsRef.current.close();
  };

  const textTitle = `Você quer compartilhar o frete com ${truckers.length} ${
    truckers.length > 1 ? 'motoristas' : 'motorista'
  }?`;

  const textDescription =
    truckers.length === 1
      ? `O motorista receberá uma mensagem no celular para ver o frete no app da Fretebras.`
      : `Os motoristas receberão uma mensagem no celular para ver o frete no app da Fretebras.`;

  const truckersSharedCountText = `${String(driverTruckerAlreadyShared).padStart(2, '0')} de ${String(
    truckers.length
  ).padStart(2, '0')}`;

  const calloutMessage =
    driverTruckerAlreadyShared === 1
      ? `${truckersSharedCountText} motorista dessa seleção já foi avisado sobre o frete.`
      : `${truckersSharedCountText} motoristas dessa seleção já foram avisados sobre o frete.`;

  const headerSuccessToast = truckers.length !== 1 ? 'Fretes compartilhados!' : 'Frete compartilhado!';

  const shouldShowCallout = useMemo(() => driverTruckerAlreadyShared > 0, [driverTruckerAlreadyShared]);

  return (
    <>
      <Styled.WrapperToast
        ref={toastSuccessRef}
        heading={headerSuccessToast}
        severity="success"
        data-testid="success-toast"
      />

      <Styled.WrapperToast
        ref={toastErrorsRef}
        message="Falha ao compartilhar o frete."
        severity="error"
        data-testid="error-toast"
      >
        <FuelButton
          variant="secondary"
          slot="button"
          size="small"
          onClick={() => handleErrorRequestToast()}
          data-testid="error-toast-try-again"
        >
          Tentar novamente
        </FuelButton>
      </Styled.WrapperToast>

      <FuelDialog.Root
        isOpen={isOpen}
        onClose={handleOnClose}
        portalName="portal-trucker-listing"
        removePortalOnDestroy={false}
        width="md"
      >
        <FuelDialog.Header>
          <FuelTypography size="heading-sm" weight="bold" color="neutral-strong">
            {textTitle}
          </FuelTypography>
        </FuelDialog.Header>
        <FuelDialog.Body>
          <FuelTypography data-testid="modal-share-freights-description">{textDescription}</FuelTypography>
          {shouldShowCallout && (
            <>
              <FuelSpacer orientation="vertical" y="km6" />
              <FuelCallout fullWidth icon="information" message={calloutMessage} data-testid="already-shared-callout" />
            </>
          )}
        </FuelDialog.Body>
        <FuelDialog.Footer>
          <FuelDialog.Action
            variant="primary"
            size="large"
            onClick={handleOpenToastSuccess}
            isLoading={requestLoadingShare}
          >
            Compartilhar
          </FuelDialog.Action>
          <FuelDialog.Action variant="tertiary" size="large" onClick={handleOnClose} isLoading={false}>
            Cancelar
          </FuelDialog.Action>
        </FuelDialog.Footer>
      </FuelDialog.Root>
    </>
  );
};

export default ModalConfirmShareFreights;
